import { friendColor } from "./friends"
import { CLIENT_CONTRACT } from "./client_contract"

// Breadcrumb trail lines on the live map: my own track while recording
// ("Show my track when recording") and per-friend trails accumulated from the
// presence payload ("Auto-track active friends"). Both are plain GeoJSON
// LineStrings in dedicated sources, styled like the other map lines.
export const WEB_TRACK_LINES_POLICY = Object.freeze({
  selfTrackSourceId: "src-self-track",
  selfTrackLayerId: "lyr-self-track",
  friendTrackSourceId: "src-friend-tracks",
  friendTrackLayerId: "lyr-friend-tracks",
  // Caps keep a long day of GPS fixes / presence polls from growing the
  // GeoJSON sources without bound; the oldest fixes drop off the tail.
  selfTrackMaxPoints: CLIENT_CONTRACT.web.map.selfTrackMaxPoints,
  friendTrackMaxPoints: CLIENT_CONTRACT.web.map.friendTrackMaxPoints,
  selfTrackColor: "#2563eb"
})

// Style-layer definitions, registered once at map boot (same shape as the
// overlay layers in map_layer_view_policy).
export function trackLineLayerDefinitions() {
  return [
    {
      id: WEB_TRACK_LINES_POLICY.friendTrackLayerId, type: "line", source: WEB_TRACK_LINES_POLICY.friendTrackSourceId,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": ["get", "color"], "line-width": 2.5, "line-opacity": 0.8 }
    },
    {
      id: WEB_TRACK_LINES_POLICY.selfTrackLayerId, type: "line", source: WEB_TRACK_LINES_POLICY.selfTrackSourceId,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": WEB_TRACK_LINES_POLICY.selfTrackColor, "line-width": 3.5, "line-opacity": 0.9 }
    }
  ]
}

// Append a [lat, lon] fix to a track, dropping the oldest points past the cap.
// Consecutive duplicates (a stationary fix re-emitted) are skipped.
export function appendTrackPoint(points = [], point = null, maxPoints = WEB_TRACK_LINES_POLICY.selfTrackMaxPoints) {
  const list = Array.from(points || [])
  if (!validLatLon(point)) return list
  const last = list[list.length - 1]
  if (last && last[0] === point[0] && last[1] === point[1]) return list
  const next = [...list, [point[0], point[1]]]
  const cap = Math.max(2, Number(maxPoints) || WEB_TRACK_LINES_POLICY.selfTrackMaxPoints)
  return next.length > cap ? next.slice(next.length - cap) : next
}

// GeoJSON LineString for my own track, or null until there is a line to draw.
export function selfTrackFeature(points = []) {
  if (!Array.isArray(points) || points.length < 2) return null
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "LineString", coordinates: points.map(([lat, lon]) => [lon, lat]) }
  }
}

// Fold one presence payload into the per-friend track accumulator (a plain
// object keyed by friend id). Only friends who are recording AND currently
// locatable extend their trail; anyone missing from the payload, off-trip, or
// without coordinates (stale/offline — the same gate the friend markers use)
// has their trail cleared. Track ids are carried so a friend starting a NEW
// trip doesn't stitch the old trail onto it.
export function friendTracksAccumulate(tracks = {}, friends = [], maxPoints = WEB_TRACK_LINES_POLICY.friendTrackMaxPoints) {
  const next = {}
  for (const f of Array.from(friends || [])) {
    if (!f || f.id == null || !f.on_trip || !validLatLon([f.lat, f.lon])) continue
    const previous = tracks[f.id] && String(tracks[f.id].tripId) === String(f.trip_id)
      ? tracks[f.id].points
      : []
    next[f.id] = { tripId: f.trip_id ?? null, points: appendTrackPoint(previous, [f.lat, f.lon], maxPoints) }
  }
  return next
}

// One LineString feature per friend, colored with the same palette as the
// friend markers so dot and trail match.
export function friendTrackFeatures(tracks = {}) {
  return Object.entries(tracks || {})
    .map(([id, track]) => {
      const feature = selfTrackFeature(track?.points)
      if (!feature) return null
      return { ...feature, properties: { color: friendColor(id) } }
    })
    .filter(Boolean)
}

function validLatLon(point) {
  return Array.isArray(point) && point.length >= 2 &&
    Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1])) &&
    point[0] != null && point[1] != null
}
